import { useEffect } from "react";

import EditJsonSchemaModal from "../EditJsonSchemaModal";

import { SelectProps } from "./types";
import {
  getSelectControlValue,
  getSelectItemValue,
  isJsonSchema,
  optionMatchesDropdownValue,
  resolveSelectOptionValue,
} from "./utils";

import { SchemaDefinition } from "@/types";
import {
  Select as SelectComponent,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Select = ({
  options,
  onChange,
  placeholder,
  defaultValue,
  selectedDropdownValue,
  isDisabled,
  readOnly,
  isPreview,
}: SelectProps) => {
  const preferredDefault = options.find(
    option => getSelectItemValue(option.value) === defaultValue,
  )?.value;

  // Keep store in sync when display falls back (stale voice=0 must not stay in state).
  useEffect(() => {
    if (isPreview || isDisabled || readOnly || options.length === 0) {
      return;
    }

    const resolved = resolveSelectOptionValue(selectedDropdownValue, options, preferredDefault);

    if (resolved === undefined) {
      return;
    }

    if (!optionMatchesDropdownValue(resolved, selectedDropdownValue)) {
      onChange(resolved);
    }
  }, [isPreview, isDisabled, readOnly, options, preferredDefault, selectedDropdownValue, onChange]);

  const handleChange = (value: string) => {
    const selectedOption = options.find(option => getSelectItemValue(option.value) === value);

    if (selectedOption) {
      onChange(selectedOption.value);
    } else {
      onChange(value);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <SelectComponent
        onValueChange={isPreview ? undefined : handleChange}
        defaultValue={defaultValue}
        value={getSelectControlValue(selectedDropdownValue, options, preferredDefault)}
        disabled={isDisabled || readOnly}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-[200px] overflow-y-auto">
          {options.map(option => (
            <SelectItem
              key={`${option.display_name}-${getSelectItemValue(option.value)}`}
              value={getSelectItemValue(option.value)}
              defaultValue={defaultValue}
            >
              {option.display_name}
            </SelectItem>
          ))}
        </SelectContent>
      </SelectComponent>

      {isJsonSchema(selectedDropdownValue) && (
        <EditJsonSchemaModal
          initialValues={{
            json: selectedDropdownValue.json_schema,
          }}
          readonly={readOnly || isPreview}
          handleSubmit={(schema: SchemaDefinition) => {
            onChange({
              type: "json_schema",
              json_schema: schema,
            });
          }}
        />
      )}
    </div>
  );
};
